import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { Button, Modal, Tooltip, Typography, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { getCourseDetail } from '../../features/courseDetail/courseDetailAction';
import { useNavigate } from 'react-router-dom';
import { useDispatch} from "react-redux";
import CustomOutlinedSwitch from '../Common/CustomOutlinedSwitch';
import { getTenantCourseDetail } from '../../features/tenantCourseDetail/tenantCourseDetailAction';

export default function CoursesTable(props) {
  const { rows} = props;
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const searchDebounceRef = useRef();
  const hasFetched = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [editToggle, setEditToggle] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCloseModal = () => {
    setShowSettingsModal(false);
  };

  const handleCourseEditAccess = () => {
    setShowSettingsModal(false);
  }

  useEffect(() => {
    if (!hasFetched.current) {
      getCourseDetailsData();
      hasFetched.current = true;
    }
  }, [])

  const getCourseDetailsData = (searchValue = "") => {
    dispatch(getCourseDetail({ search: !!searchValue, searchText: searchValue }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      getCourseDetailsData(value);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchText("");
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    getCourseDetailsData("");
  };

  const handleSearch = () => {
    getCourseDetailsData(searchText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenSettingsModal = (id) => {
    setShowSettingsModal(true);
  };

  const handleRedirectTenantDetailSceen = (id) => {
    dispatch(getTenantCourseDetail({ id }));
    navigate(`/customer/access/${id}`, { state: id })
  }

  const columns = [
    {
      field: 'courseTitle',
      headerName: 'Course Title',
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: '#1976d2',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {params.row.courseTitle}
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 0.6,
    },
    {
      field: 'noOfTenants',
      headerName: 'Allowed License',
      flex: 0.6,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Settings" arrow>
            <IconButton
              sx={{ color: '#1976d2', marginLeft: '-10px' }}
              onClick={() => handleOpenSettingsModal(params.row.id)}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Customers" arrow>
            <IconButton sx={{ color: '#1976d2' }}
              onClick={() => handleRedirectTenantDetailSceen(params.row.id)}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', padding: '20px', cursor: 'pointer', marginTop: '50px' }}>
      <Box sx={{ width: '300px', margin: '0px 0 20px 0px' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search course"
          value={searchText}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchText ? (
                  <IconButton onClick={handleSearchClear}>
                    <CloseIcon />
                  </IconButton>
                ) : (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        disableColumnReorder
        disableSelectionOnClick
        pageSizeOptions={[20]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        sx={{
          width: '100%',
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700",
            color: '#1976d2',
          },
        }}
      />
      {showSettingsModal && <>
        <Modal open={showSettingsModal} onClose={handleCloseModal}>
          <Box style={{ borderRadius: '4px', position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "white", boxShadow: 24, p: 4, width: '640px' }}>
            <Box sx={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px', }}>
              <Typography variant="h6" component="h2" sx={{ color: '#000', textAlign: 'left', fontWeight: '600', fontSize: '24px' }}>
                Allow Edit Access
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <CustomOutlinedSwitch
                  checked={editToggle}
                  onChange={(e) => setEditToggle(e.target.checked)}
                />
                <Typography variant="h6" sx={{ fontWeight: '500', fontSize: '16px', color: '#111928' }}>
                  Only when this toggle is on, users will have permission to edit courses.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: '20px', marginTop: '20px' }}>
                <Button variant="contained" onClick={handleCloseModal} sx={{ textTransform: 'capitalize', fontWeight: '600', background: 'transparent', border: '1px solid #1976d2', color: '#1976d2' }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleCourseEditAccess} sx={{ textTransform: 'capitalize', fontWeight: '600' }}>
                  Yes Allow
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </>}
    </Box>
  );
}